import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Trash, X, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_COLOR = { admin: "#FF3B30", analyst: "#00F0FF", viewer: "#71717A" };
const ROLES = ["admin", "analyst", "viewer"];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "analyst" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error("Failed to load users");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      setUsers((p) => [...p, data]);
      setShowForm(false);
      setForm({ name: "", email: "", password: "", role: "analyst" });
      toast.success("Operator added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (id, role) => {
    try {
      const { data } = await api.patch(`/users/${id}`, { role });
      setUsers((p) => p.map((u) => (u.id === id ? { ...u, role: data.role } : u)));
      toast.success("Role updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      setUsers((p) => p.filter((u) => u.id !== id));
      toast.success("Operator removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Administration</p>
          <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Operator Management</h1>
        </div>
        <button
          data-testid="add-user-button"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#00F0FF] text-black font-bold uppercase tracking-[0.15em] text-[11px] px-4 py-2.5 hover:bg-white active:scale-[0.98] transition-all duration-150"
        >
          <UserPlus size={14} weight="bold" /> Add Operator
        </button>
      </div>

      <div className="bg-[#0C0C14] border border-[#1F1F33] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="users-table">
            <thead>
              <tr className="border-b border-[#1F1F33] text-[10px] uppercase tracking-[0.15em] text-[#71717A]">
                <th className="px-5 py-3 font-medium">Operator</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className={`border-b border-[#1F1F33] text-sm ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#12121E] border border-[#1F1F33] flex items-center justify-center text-[#00F0FF] text-xs font-bold">
                        {(u.name || u.email).slice(0, 1).toUpperCase()}
                      </div>
                      <span className="font-medium">{u.name || "—"}</span>
                      {u.id === me?.id && <span className="text-[9px] uppercase tracking-wider text-[#71717A]">(you)</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-[#A1A1AA]">{u.email}</td>
                  <td className="px-5 py-3">
                    <select
                      data-testid={`role-select-${u.email}`}
                      value={u.role}
                      onChange={(e) => changeRole(u.id, e.target.value)}
                      className="bg-[#05050A] border rounded-none px-2 py-1 text-xs uppercase tracking-wider outline-none focus:border-[#00F0FF]"
                      style={{ color: ROLE_COLOR[u.role], borderColor: `${ROLE_COLOR[u.role]}55` }}
                    >
                      {ROLES.map((r) => <option key={r} value={r} style={{ color: "#fff", background: "#0C0C14" }}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      data-testid={`delete-user-${u.email}`}
                      onClick={() => remove(u.id)}
                      disabled={u.id === me?.id}
                      className="text-[#71717A] hover:text-[#FF3B30] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="user-form-modal">
          <form onSubmit={create} className="w-full max-w-md bg-[#0C0C14] border border-[#1F1F33] p-6 fade-up font-mono">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} color="#00F0FF" weight="duotone" />
                <h2 className="font-head text-xl tracking-tight font-bold">Add Operator</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="text-[#71717A] hover:text-white"><X size={20} /></button>
            </div>

            {["name", "email", "password"].map((f) => (
              <div key={f} className="mb-4">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">{f}</label>
                <input
                  data-testid={`user-${f}-input`}
                  type={f === "password" ? "password" : f === "email" ? "email" : "text"}
                  required
                  value={form[f]}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  className="w-full bg-[#05050A] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] transition-colors"
                />
              </div>
            ))}

            <div className="mb-6">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Role</label>
              <select
                data-testid="user-role-select"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-[#05050A] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF]"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <button
              data-testid="user-submit-button"
              type="submit" disabled={busy}
              className="w-full bg-[#00F0FF] text-black font-bold uppercase tracking-[0.2em] text-xs py-3 hover:bg-white active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
            >
              {busy ? "Adding…" : "Add Operator"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
